import React, { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { theme } from "../theme.js";
import LoadingOverlay from "../components/LoadingOverlay.jsx";
import Button from "../components/base/Button.jsx";
import { QRCodeSVG as QRCode } from "qrcode.react";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

const toDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const API_URL = process.env.REACT_APP_API_URL;

export default function TicketDetail() {
  const { ticket_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [race, setRace] = useState(null);
  const [ticketsInRace, setTicketsInRace] = useState([]);
  const [index, setIndex] = useState(0);
  const [showSellConfirm, setShowSellConfirm] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sendEmail, setSendEmail] = useState("");
  // Note: ticket_id is now public_id

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch ticket + race details (adjust backend endpoints if needed)
        const res = await fetch(`${API_URL}/api/tickets/${ticket_id}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const fetched = data.ticket || data;
        setTicket(fetched);
        setRace(data.race || data.race_details || fetched?.race || null);
        // If navigation state provides bibs or bibIds, use them to avoid extra fetch
        const nav = location?.state || {};
        if (nav.bibs && Array.isArray(nav.bibs) && nav.bibs.length) {
          setTicketsInRace(nav.bibs);
          const pos = nav.bibs.findIndex((b) => (b.id_bdd || b.id) === (fetched.id_bdd || fetched.id || Number(ticket_id)));
          setIndex(pos >= 0 ? pos : 0);
        } else if (nav.bibIds && Array.isArray(nav.bibIds) && nav.bibIds.length) {
          // fetch each ticket by id
          try {
            const promises = nav.bibIds.map((tid) => fetch(`${API_URL}/api/tickets/${tid}`).then(r => r.ok ? r.json() : null));
            const results = await Promise.all(promises);
            const tickets = results.filter(Boolean).map(r => r.ticket || r);
            if (tickets.length) {
              setTicketsInRace(tickets);
              const pos = tickets.findIndex((b) => (b.id_bdd || b.id) === (fetched.id_bdd || fetched.id || Number(ticket_id)));
              setIndex(pos >= 0 ? pos : 0);
            }
          } catch (e) {
            console.warn("Failed to fetch tickets from bibIds", e);
          }
        } else {
          // Try to fetch all user's tickets and group those for the same race
          try {
            const userData = localStorage.getItem("user");
            const parsedUser = userData ? JSON.parse(userData) : null;
            if (parsedUser?.email) {
              const r2 = await fetch(`${API_URL}/api/tickets/user/${encodeURIComponent(parsedUser.email)}`);
              if (r2.ok) {
                const list = await r2.json();
                const combined = [...(list.following || []), ...(list.previous || [])];
                const sameRace = combined.filter((b) => b?.race && fetched?.race && (b.race.id_bdd ?? b.race.id) === (fetched.race.id_bdd ?? fetched.race.id));
                if (sameRace.length) {
                  setTicketsInRace(sameRace);
                  const pos = sameRace.findIndex((b) => (b.id_bdd || b.id) === (fetched.id_bdd || fetched.id || Number(ticket_id)));
                  setIndex(pos >= 0 ? pos : 0);
                }
              }
            }
          } catch (e) {
            console.warn("Could not fetch user's tickets for race", e);
          }
        }
        setLoading(false);
      } catch (e) {
        console.error("Failed to fetch ticket:", e);
        setError("Impossible de charger le billet");
        setLoading(false);
      }
    };
    fetchData();
  }, [ticket_id, location]);

  const handleSell = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/tickets/resell/${ticket_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setShowSellConfirm(false);
      setLoading(false);
      navigate("/user", { state: { tab: "transactions" } });
    } catch (e) {
      console.error("Sell failed:", e);
      setError("La revente a échoué");
      setLoading(false);
    }
  };

  const handleSend = async () => {
    try {
      if (!sendEmail) return;
      setLoading(true);
      const res = await fetch(`${API_URL}/api/tickets/transfer/${ticket_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: sendEmail }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setShowSendDialog(false);
      setLoading(false);
      navigate("/user", { state: { tab: "transactions" } });
    } catch (e) {
      console.error("Send failed:", e);
      setError("L'envoi a échoué");
      setLoading(false);
    }
  };

  const handleGetBack = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/tickets/cancel/${ticket_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setShowSendDialog(false);
      setLoading(false);
      navigate("/user", { state: { tab: "races" } });
    } catch (e) {
      console.error("Send failed:", e);
      setError("La récupération a échoué");
      setLoading(false);
    }
  };


  if (error) {
    return <div style={styles.page}><div style={styles.card}>{error}</div></div>;
  }

  const selected = ticketsInRace && ticketsInRace.length ? ticketsInRace[index] : ticket;
  const qrPayload = String(selected?.public_id || ticket_id);
  const isRacePassed = race && new Date(race.date) < new Date();

  return (
    <div style={styles.page}>
      <div style={styles.pageInner}>
        <button style={styles.backButton} onClick={() => navigate("/user")}>
          <ArrowLeftIcon style={{ width: 16, height: 16 }} />
          Retour
        </button>
        <div style={styles.card}>
          <p style={styles.subtitle}>Présentez ce QR code pour retirer votre dossard.</p>

        <div style={styles.qrWrapper}>
          {ticketsInRace && ticketsInRace.length > 1 && (
            <button style={styles.largeArrowButton} onClick={() => setIndex((i) => (i - 1 + ticketsInRace.length) % ticketsInRace.length)}>‹</button>
          )}
          <div style={{position: 'relative', display: 'inline-block'}}>
            <QRCode value={qrPayload} size={isMobile ? 200 : 280} style={{filter: (selected?.status === "Sold" || selected?.status === "Waiting transfer" || isRacePassed || !selected?.registration_complete) ? 'blur(5px)' : 'none'}} />
          </div>
          {ticketsInRace && ticketsInRace.length > 1 && (
            <button style={styles.largeArrowButton} onClick={() => setIndex((i) => (i + 1) % ticketsInRace.length)}>›</button>
          )}
        </div>

        {ticketsInRace && ticketsInRace.length > 1 && (
          <div style={styles.smallInfo}>
            <div>Dossard #{selected?.bib_number || selected?.number || selected?.id_bdd} — {selected?.status}</div>
            <div style={{marginTop: theme.spacing.xs, marginBottom: theme.spacing.xl}}>{index + 1} / {ticketsInRace.length}</div>
          </div>
        )}
      {selected?.status==="Purchased" &&
        <div style={styles.actions}>
          <Button title="Revendre le billet" onClick={() => setShowSellConfirm(true)} disabled={isRacePassed} />
          <Button title="Envoyer à quelqu'un" onClick={() => setShowSendDialog(true)} disabled={isRacePassed} />
        </div>
      }
      {selected?.status==="For Sale" &&
      <>
      <p style={{ color: theme.colors.error, fontWeight: theme.fonts.weight.semibold, textAlign: "center", marginBottom: theme.spacing.sm }}>Ce billet est actuellement en revente.</p>
      <Button title="Annuler la vente du billet" onClick={handleGetBack}/>
      </>
      }
      {selected?.status==="Waiting transfer" &&
      <>
      <p style={{ color: theme.colors.warning, fontWeight: theme.fonts.weight.semibold, textAlign: "center", marginBottom: theme.spacing.sm }}>Ce billet a été transféré récemment. Veuillez vérifier vos emails pour accepter le transfert.</p>
      <Button title="Annuler le transfert du billet" onClick={handleGetBack} />
      </>
      }
      {selected?.status==="Sold" &&
      <p style={{ color: theme.colors.success, fontWeight: theme.fonts.weight.semibold, textAlign: "center" }}>Ce billet a été revendu avec succès.</p>
      }
      {selected?.status === "Purchased" && !selected?.registration_complete && (
        <div style={styles.incompleteWarning}>
          <h3 style={styles.incompleteTitle}>Inscription Incomplète</h3>
          <p style={styles.incompleteText}>Votre inscription n'est pas complète. Veuillez soumettre tous les documents requis pour finaliser votre participation.</p>
        </div>
      )}

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Détails du Billet</h2>
          <ul style={styles.detailsList}>
            <li><strong>Numéro de dossard:</strong> {selected?.bib_number || selected?.ticket_number || selected?.number || "—"}</li>
            {selected?.purchase_date && (
              <li><strong>Date d'achat:</strong> {new Date(selected.purchase_date).toLocaleString('fr-FR')}</li>
            )}
          </ul>
        </div>

        <div style={styles.section}>
          {race ? (
            <>
              <div style={{marginBottom: '12px'}}>
                <p style={{margin: '4px 0', fontSize: '14px'}}><strong>Participant:</strong> {selected?.participant?.firstname || "—"} {selected?.participant?.name || "—"}</p>
                <p style={{margin: '4px 0', fontSize: '14px'}}><strong>Course:</strong> <span style={{cursor: 'pointer', color: theme.colors.primary, textDecoration: 'underline'}} onClick={() => navigate(`/events/${race.event_id}/races/${race.id_bdd || race.id}`)}>{race.name}</span></p>
              </div>
              <div style={{textAlign: 'center'}}>
                {selected?.status === "Purchased" && !isRacePassed && (
                  <button style={styles.showMoreButton} onClick={() => {
                    navigate(`/events/${race.event_id}/races/${race.id_bdd || race.id}/${selected?.price_id}/register`, {
                      state: { editMode: true, participant: selected?.participant, bib_id: selected?.id_bdd || selected?.id }
                    });
                  }}>
                    Gérer l'inscription
                  </button>
                )}
              </div>
            </>
          ) : (
            <p>Aucun détail de course disponible.</p>
          )}
        </div>
      </div>

      {showSellConfirm && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Confirmer la revente</h3>
            <p>Le billet sera remis en vente et vous ne pourrez plus l'utiliser.</p>
            <p>Vous recevrez si quelqu'un l'achète, 75% du montant du dossard remboursé sur votre carte.</p>
            <p style={{color: theme.colors.error}}>Attention : en cas de revente, les options ne vous seront pas remboursés.</p>
            <div style={styles.modalActions}>
              <button style={{ ...styles.button, ...styles.cta }} onClick={handleSell}>Revendre</button>
              <button style={styles.button} onClick={() => setShowSellConfirm(false)}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {showSendDialog && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Envoyer le billet</h3>
            <p>Entrez l'email du destinataire. Il recevra un lien pour accepter le transfert.</p>
            <input
              type="email"
              placeholder="email@exemple.com"
              value={sendEmail}
              onChange={(e) => setSendEmail(e.target.value)}
              style={styles.input}
            />
            <div style={styles.modalActions}>
              <button style={{ ...styles.button, ...styles.cta }} onClick={handleSend}>Envoyer</button>
              <button style={styles.button} onClick={() => setShowSendDialog(false)}>Annuler</button>
            </div>
          </div>
        </div>
      )}
      <LoadingOverlay isVisible={loading} message="Chargement du ticket..." />
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    background: theme.colors.bgDefault,
    padding: theme.spacing.lg,
  },
  pageInner: {
    width: "100%",
    maxWidth: 720,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.sm,
  },
  card: {
    width: "100%",
    maxWidth: 720,
    background: theme.colors.white,
    borderRadius: theme.radius.xl,
    boxShadow: theme.shadows.lg,
    padding: theme.spacing.lg,
  },
  backButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: theme.colors.dark,
    fontWeight: theme.fonts.weight.semibold,
    fontSize: theme.fonts.size.sm,
    padding: 0,
    marginBottom: theme.spacing.sm,
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing.xs,
  },
  title: { margin: 0, fontSize: theme.fonts.size.xl },
  subtitle: { color: theme.colors.textInverted, marginTop: theme.spacing.xs },
  qrWrapper: { display: "flex", justifyContent: "center", alignItems: "center", gap: theme.spacing.lg, padding: theme.spacing.huge },
  actions: { display: "flex", gap: theme.spacing.md, justifyContent: "center", marginTop: theme.spacing.xs },
  button: {
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    background: theme.colors.dark,
    color: theme.colors.white,
    border: "none",
    borderRadius: theme.radius.lg,
    cursor: "pointer",
    fontWeight: theme.fonts.weight.semibold,
  },
  cta: { 
    background: theme.colors.light,
    color : theme.colors.textInverted,
   },
  section: { marginTop: theme.spacing.lg },
  sectionTitle: { margin: 0, fontSize: theme.fonts.size.lg },
  detailsList: { listStyle: "none", padding: 0, marginTop: theme.spacing.md, lineHeight: 1.8 },
  showMoreButton: {
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    background: theme.colors.light,
    color: theme.colors.textInverted,
    border: "none",
    borderRadius: theme.radius.md,
    cursor: "pointer",
    fontWeight: theme.fonts.weight.semibold,
    fontSize: theme.fonts.size.sm,
    marginTop: theme.spacing.md,
  },
  incompleteWarning: {
    backgroundColor: "#fff3cd",
    borderLeft: `4px solid #ffc107`,
    padding: theme.spacing.md,
    marginTop: theme.spacing.md,
    borderRadius: theme.radius.md,
  },
  incompleteTitle: {
    margin: `0 0 ${theme.spacing.xs} 0`,
    color: "#856404",
    fontSize: theme.fonts.size.base,
    fontWeight: theme.fonts.weight.semibold,
  },
  incompleteText: {
    margin: 0,
    color: "#856404",
    fontSize: theme.fonts.size.sm,
    lineHeight: 1.5,
  },
  modalBackdrop: {
    position: "fixed", 
    top: 0, left: 0, right: 0, bottom: 0,
    background: theme.colors.modalBackdrop, 
    display: "grid", 
    placeItems: "center",
  },
  modal: { 
    background: theme.colors.white, 
    borderRadius: theme.radius.lg, 
    padding: theme.spacing.lg, 
    width: 420, 
    maxWidth: "90vw",
    border: `2px solid ${theme.colors.border}`, 
    boxShadow: theme.shadows.xl,    
    opacity: 0.97,
    },
  modalTitle: { 
    marginTop: 0,
    marginBottom: theme.spacing.sm, 
  },
  modalActions: { display: "flex", gap: theme.spacing.md, justifyContent: "flex-end", marginTop: theme.spacing.md },
  input: {
    width: "100%", padding: theme.spacing.md, borderRadius: theme.radius.md, border: `1px solid ${theme.colors.border}`, marginTop: theme.spacing.md,
  },
  largeArrowButton: { 
    background: theme.colors.white, 
    border: "none", 
    borderRadius: theme.radius.md,
    padding: `${theme.spacing.md} ${theme.spacing.lg}`, 
    cursor: "pointer", 
    fontSize: 32, 
    fontWeight: "bold",
    color: theme.colors.dark,
    minWidth: 50, 
    minHeight: 50, 
    display: "flex", 
    alignItems: "center", 
    justifyContent: "center", 
    verticalAlign: "middle",
  },
  smallInfo: { 
    textAlign: "center", 
    marginTop: theme.spacing.md, 
    color: theme.colors.textInverted, 
    fontSize: theme.fonts.size.xs 
  },
};
